import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import {
  installationCreateStep1FieldPaths,
  installationCreateStepLatinFieldPaths,
  installationCreateStepLocalFieldPaths
} from '@/features/installations/components/installation-create/constants'
import {
  applyInstallationCreateZodIssuesToForm,
  syncLatinAddressToLocalFormFields,
} from '@/features/installations/components/installation-create/form-utils'
import { InstallationCreateStepAddressLatin } from '@/features/installations/components/installation-create/installation-create-step-address-latin'
import { InstallationCreateStepAddressLocal } from '@/features/installations/components/installation-create/installation-create-step-address-local'
import { InstallationCreateStepGeneral } from '@/features/installations/components/installation-create/installation-create-step-general'
import { InstallationCreateStepReview } from '@/features/installations/components/installation-create/installation-create-step-review'
import { InstallationCreateWizardNav } from '@/features/installations/components/installation-create/installation-create-wizard-nav'
import { InstallationCreateWizardStepsOverview } from '@/features/installations/components/installation-create/installation-create-wizard-steps-overview'
import type { InstallationCreateWizardStepMeta } from '@/features/installations/components/installation-create/types'
import {
  createInstallationCreateFormSchema,
  createInstallationCreateStep1Schema,
  getInstallationCreateFormDefaultValues,
  installationLatinAddressSchema,
  installationLocalAddressSchema,
  mapInstallationCreateFormToApiPayload,
  type InstallationCreateFormInput
} from '@/features/installations/installation-create.schema'
import {
  pickDefaultInstallationId,
  readLastInstallationId,
  writeLastInstallationId
} from '@/features/installations/lib/last-installation-id'
import {
  useCreateInstallation,
  useInstallations
} from '@/features/installations/hooks/useInstallations'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { useSession } from '@/shared/hooks/useSession'
import { resolveSelectableInstallationCountryCode } from '@/shared/config/countries'
import { XCircleIcon } from 'lucide-react'

export function InstallationCreatePage() {
  const { t } = useTranslation('installations')
  const navigate = useNavigate()
  const { checkPermission } = usePermissions()
  const {
    session: { organization }
  } = useSession()
  const { data: installations = [] } = useInstallations()
  const { mutateAsync: createInstallation, isPending: isCreating } = useCreateInstallation()

  const placeOfRegistration = organization.placeOfRegistration ?? ''
  const unlocodeCountryFilter = useMemo(() => {
    const trimmed = placeOfRegistration.trim().toUpperCase()
    if (trimmed.length !== 2) {
      return null
    }
    return trimmed
  }, [placeOfRegistration])
  const requiresLocalNameAndAddress = useDoesOrganizationRequiresLocalAddress(placeOfRegistration)

  const defaultCountryCode = useMemo(() => {
    const code = placeOfRegistration.trim()
    return resolveSelectableInstallationCountryCode(code)
  }, [placeOfRegistration])

  const formSchema = useMemo(
    () => createInstallationCreateFormSchema({ requiresLocalNameAndAddress }),
    [requiresLocalNameAndAddress]
  )

  const form = useForm<InstallationCreateFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: getInstallationCreateFormDefaultValues(defaultCountryCode),
    mode: 'onSubmit'
  })

  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    form.reset(getInstallationCreateFormDefaultValues(defaultCountryCode))
    setStepIndex(0)
  }, [defaultCountryCode, form])

  const stepMetas = useMemo((): InstallationCreateWizardStepMeta[] => {
    const general = {
      key: 'general' as const,
      title: t('create.wizard.steps.general.title'),
      description: t('create.wizard.steps.general.description')
    }
    const addressLatin = {
      key: 'addressLatin' as const,
      title: t('create.wizard.steps.addressLatin.title'),
      description: t('create.wizard.steps.addressLatin.description')
    }
    const addressLocal = {
      key: 'addressLocal' as const,
      title: t('create.wizard.steps.addressLocal.title'),
      description: t('create.wizard.steps.addressLocal.description')
    }
    const review = {
      key: 'review' as const,
      title: t('create.wizard.steps.review.title'),
      description: t('create.wizard.steps.review.description')
    }
    if (requiresLocalNameAndAddress) {
      return [general, addressLatin, addressLocal, review]
    }
    return [general, addressLatin, review]
  }, [requiresLocalNameAndAddress, t])

  const lastStepIndex = Math.max(0, stepMetas.length - 1)

  const currentStepMeta = stepMetas[stepIndex]
  const currentStepHeading = currentStepMeta != null ? currentStepMeta.title : t('create.title')

  const canCreate = checkPermission('installation', 'create')
  const atCap = installations.length >= organization.maxNumberInstallations

  const dashboardLink = useMemo(() => {
    if (installations.length === 0) {
      return '/'
    }
    const id = pickDefaultInstallationId(installations, readLastInstallationId())
    return id != null ? `/dashboard/${id}` : '/'
  }, [installations])

  const handleCreateInstallation = form.handleSubmit(async values => {
    const parsed = formSchema.parse(values)
    const payload = mapInstallationCreateFormToApiPayload(parsed, {
      requiresLocalNameAndAddress
    })
    try {
      const created = await createInstallation(payload)
      writeLastInstallationId(created.id)
      toast.success(t('create.toast.created'))
      navigate(`/dashboard/${created.id}`)
    } catch {
      toast.error(t('create.toast.createFailed'))
    }
  })

  const handleWizardNext = () => {
    const values = form.getValues()

    if (stepIndex === 0) {
      form.clearErrors([...installationCreateStep1FieldPaths])
      const step1Schema = createInstallationCreateStep1Schema({
        requiresLocalNameAndAddress
      })
      const parsed = step1Schema.safeParse(values)
      if (!parsed.success) {
        applyInstallationCreateZodIssuesToForm(form, parsed.error.issues)
        return
      }
    }

    if (stepIndex === 1) {
      form.clearErrors([...installationCreateStepLatinFieldPaths])
      const parsed = installationLatinAddressSchema.safeParse({
        street: values.street,
        street2: values.street2,
        streetNumber: values.streetNumber,
        country: values.country,
        postCode: values.postCode,
        poBox: values.poBox,
        city: values.city,
        unlocode: values.unlocode,
        longitude: values.longitude,
        latitude: values.latitude
      })
      if (!parsed.success) {
        applyInstallationCreateZodIssuesToForm(form, parsed.error.issues)
        return
      }
      if (!requiresLocalNameAndAddress) {
        syncLatinAddressToLocalFormFields(form)
      }
    }

    if (stepIndex === 2 && requiresLocalNameAndAddress) {
      form.clearErrors([...installationCreateStepLocalFieldPaths])
      const parsed = installationLocalAddressSchema.safeParse({
        country: values.country,
        localStreet: values.localStreet,
        localStreet2: values.localStreet2,
        localStreetNumber: values.localStreetNumber,
        localPostCode: values.localPostCode,
        localPoBox: values.localPoBox,
        localCity: values.localCity
      })
      if (!parsed.success) {
        applyInstallationCreateZodIssuesToForm(form, parsed.error.issues)
        return
      }
    }

    setStepIndex(index => Math.min(lastStepIndex, index + 1))
  }

  const handleWizardBack = () => {
    setStepIndex(index => Math.max(0, index - 1))
  }

  const showGeneralStep = stepIndex === 0
  const showAddressLatinStep = stepIndex === 1
  const showAddressLocalStep = stepIndex === 2 && requiresLocalNameAndAddress
  const showReviewStep = stepIndex === lastStepIndex

  if (!canCreate) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-background p-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>{t('create.forbiddenTitle')}</CardTitle>
            <CardDescription>{t('create.forbiddenDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to={dashboardLink}>{t('create.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (atCap) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-background p-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>{t('create.capTitle')}</CardTitle>
            <CardDescription>{t('create.capDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to={dashboardLink}>{t('create.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-svh flex-col bg-background p-6">
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('create.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('create.subtitle')}</p>
            <p className="text-muted-foreground mt-1 text-sm font-medium">{currentStepHeading}</p>
          </div>
          <Link to={dashboardLink}>
            <XCircleIcon className="size-6 text-muted-foreground hover:text-foreground transition-colors" />
          </Link>
        </div>

        <InstallationCreateWizardStepsOverview stepMetas={stepMetas} currentStepIndex={stepIndex} />

        <Form {...form}>
          <form
            onSubmit={event => {
              event.preventDefault()
            }}
            className="flex flex-col gap-6"
          >
            {showGeneralStep && (
              <InstallationCreateStepGeneral
                form={form}
                requiresLocalNameAndAddress={requiresLocalNameAndAddress}
                placeOfRegistration={placeOfRegistration}
              />
            )}
            {showAddressLatinStep && (
              <InstallationCreateStepAddressLatin
                form={form}
                unlocodeCountryFilter={unlocodeCountryFilter}
              />
            )}
            {showAddressLocalStep && <InstallationCreateStepAddressLocal form={form} />}
            {showReviewStep && (
              <InstallationCreateStepReview
                form={form}
                requiresLocalNameAndAddress={requiresLocalNameAndAddress}
                placeOfRegistration={placeOfRegistration}
              />
            )}

            <InstallationCreateWizardNav
              stepIndex={stepIndex}
              lastStepIndex={lastStepIndex}
              dashboardLink={dashboardLink}
              isCreating={isCreating}
              onBack={handleWizardBack}
              onNext={handleWizardNext}
              onCreate={() => {
                void handleCreateInstallation()
              }}
            />
          </form>
        </Form>
      </div>
    </div>
  )
}
